import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, SidePanelFormControl, TabPaneControl } from "spark-core-dx/components";
import { useResourceDomainContext } from "../../resource/ResourceDomainProvider";
import { RESOURCES_PARTNER_GROUPS_PATH } from "../../resource/resourceRoutes";
import "../../resource/workspace/ResourceWorkspace.scss";

export const EDI_TRADING_PARTNER_ICON = "clientscreen";

const textField = (fieldName, placeholder, ariaLabel) => ({
  FieldName: fieldName,
  DisplayName: "",
  FieldType: "TextBox",
  PlaceHolder: placeholder,
  AriaLabel: ariaLabel
});

const getValue = (item, ...keys) => {
  if (!item) return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(item, key) && item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }
  return undefined;
};

const getString = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return value === undefined || value === null ? "" : String(value);
};

const getArray = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return Array.isArray(value) ? value : [];
};

const firstItem = (value) => {
  if (Array.isArray(value)) return value[0] || null;
  if (Array.isArray(value?.Items)) return value.Items[0] || null;
  if (Array.isArray(value?.items)) return value.items[0] || null;
  return value || null;
};

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");
const getOperationItems = (response) => getArray(response, "Items", "items", "Data", "data");
const getOperationSuccess = (response) => response?.Success !== false && response?.success !== false;
const getMetadataFields = (metadata) => getArray(metadata, "Fields", "fields");
const getPartnerName = (partner) => getString(partner, "PartnerName", "partnerName", "DisplayName", "displayName", "Name", "name") || "Trading Partner";
const getPartnerCode = (partner) => getString(partner, "PartnerCode", "partnerCode", "PartnerIdentifier", "partnerIdentifier", "TradingPartnerId", "tradingPartnerId");
const getPartnerExternalId = (partner) => getString(partner, "ExternalPartnerId", "externalPartnerId", "ExternalId", "externalId");
const getDescription = (item) => getString(item, "Description", "description");

const createPartnerDraft = () => ({
  IsNew: true,
  IsActive: true,
  PartnerName: "",
  PartnerCode: ""
});

const filterRows = (rows, searchText, getName, getCode) => {
  const normalizedSearch = (searchText || "").trim().toLowerCase();
  if (!normalizedSearch) return rows;
  return rows.filter((row) => `${getName(row)} ${getCode(row)}`.toLowerCase().includes(normalizedSearch));
};

const withTimeout = (promise, label, timeoutMs = 12000) => new Promise((resolve, reject) => {
  const timeout = window.setTimeout(() => reject(new Error(`${label} did not finish.`)), timeoutMs);
  Promise.resolve(promise)
    .then((value) => {
      window.clearTimeout(timeout);
      resolve(value);
    })
    .catch((error) => {
      window.clearTimeout(timeout);
      reject(error);
    });
});

const buildBackUrlSearch = (location) => {
  const currentUrl = `${location.pathname}${location.search || ""}`;
  return `?backUrl=${encodeURIComponent(currentUrl)}`;
};

const EdiTradingPartnerConnectivityManage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resourceDomainContext = useResourceDomainContext();
  const partnerManager = resourceDomainContext?.managers?.ediTradingPartner;
  const partnerManagerRef = useRef(partnerManager);
  const [partners, setPartners] = useState([]);
  const [partnerMetadata, setPartnerMetadata] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const backUrl = searchParams.get("backUrl");
  const hasPartnerManager = Boolean(partnerManager);

  useEffect(() => {
    partnerManagerRef.current = partnerManager;
  }, [partnerManager]);

  const loadRows = useCallback(async () => {
    const activePartnerManager = partnerManagerRef.current;

    if (!activePartnerManager) {
      setLoading(false);
      setError("Trading partner manager is not available from the Resource domain.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [partnerMetadataResult, partnerResult] = await Promise.all([
        withTimeout(activePartnerManager.GetMetadata?.({}), "Trading partner metadata"),
        withTimeout(activePartnerManager.Query?.({ model: "x => x.IsDeleted == false" }), "Trading partner list")
      ]);

      setPartnerMetadata(firstItem(partnerMetadataResult));
      setPartners(getOperationItems(partnerResult).filter((partner) => getValue(partner, "IsDeleted", "isDeleted") !== true));
    } catch (loadError) {
      setError(loadError?.message || "Trading partner configuration could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [hasPartnerManager, loadRows]);

  const savePartner = useCallback(async (partner) => {
    setSaveError("");
    const activePartnerManager = partnerManagerRef.current;
    const response = await activePartnerManager?.saveItem(partner);
    if (!getOperationSuccess(response)) {
      setSaveError("Trading partner could not be saved.");
      return;
    }
    setEditingPartner(null);
    await loadRows();
  }, [loadRows]);

  const deletePartner = useCallback(async () => {
    if (!deletingPartner) return;

    setSaveError("");
    const activePartnerManager = partnerManagerRef.current;
    const partnerId = getId(deletingPartner);
    const response = await activePartnerManager?.saveItem({
      ...deletingPartner,
      IsActive: false,
      IsDeleted: true,
      IsNew: false
    });

    if (!response || !getOperationSuccess(response)) {
      setSaveError("Trading partner could not be deleted.");
      return;
    }

    setDeletingPartner(null);
    setPartners((currentPartners) => currentPartners.filter((partner) => getId(partner) !== partnerId));
    await loadRows();
  }, [deletingPartner, loadRows]);

  const filteredPartners = useMemo(
    () => filterRows(partners, searchText, getPartnerName, getPartnerCode),
    [partners, searchText]
  );

  const goBack = useCallback(() => {
    if (backUrl) navigate(backUrl);
  }, [backUrl, navigate]);

  const openPartnerGroups = useCallback(() => {
    navigate(`${RESOURCES_PARTNER_GROUPS_PATH}${buildBackUrlSearch(location)}`);
  }, [location, navigate]);

  const activePartnerCount = useMemo(
    () => partners.filter((partner) => getValue(partner, "IsActive", "isActive") !== false).length,
    [partners]
  );

  const searchControl = (
    <FieldControl
      value={searchText}
      onChange={(value) => setSearchText(value ?? "")}
      fieldMetadata={textField("TradingPartnerSearch", "Search partners...", "Search partners")}
    />
  );

  const emptyOrLoadingContent = (emptyText) => {
    if (loading) {
      return (
        <div className="resource-workspace-empty-state resource-workspace-empty-state--compact">
          <Loading />
          <span>Loading trading partners.</span>
        </div>
      );
    }

    if (error) {
      return <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div>;
    }

    return (
      <div className="resource-workspace-empty-state resource-workspace-empty-state--compact">
        <span>{emptyText}</span>
      </div>
    );
  };

  const partnerRows = filteredPartners.length > 0 ? (
    <div className="resource-workspace-record-list">
      {filteredPartners.map((partner) => (
        <article key={getId(partner)} className="resource-workspace-configuration-linked-row resource-workspace-entity-object-card resource-workspace-edi-trading-partner-row">
          <span className="resource-workspace-configuration-linked-copy resource-workspace-entity-object-card__copy">
            <strong>{getPartnerName(partner)}</strong>
            <small>{getDescription(partner) || "Reusable EDI trading partner for inbound and outbound partner scopes."}</small>
          </span>
          <dl className="resource-workspace-entity-object-card__facts">
            <div>
              <dt>Status</dt>
              <dd>{getValue(partner, "IsActive", "isActive") === false ? "Inactive" : "Active"}</dd>
            </div>
            <div>
              <dt>Partner Code</dt>
              <dd>{getPartnerCode(partner) || "No partner code"}</dd>
            </div>
            <div>
              <dt>External Id</dt>
              <dd>{getPartnerExternalId(partner) || "No external partner id"}</dd>
            </div>
          </dl>
          <span className="resource-workspace-entity-object-card__actions">
            <ButtonControl type="standard-small" title={`Edit ${getPartnerName(partner)}`} aria-label={`Edit ${getPartnerName(partner)}`} onClick={() => setEditingPartner(partner)}>
              <IconControl iconKey="edit" title={`Edit ${getPartnerName(partner)}`} width="1rem" height="1rem" />
            </ButtonControl>
            <ButtonControl className="resource-workspace-row-action--danger" type="standard-small" title={`Delete ${getPartnerName(partner)}`} aria-label={`Delete ${getPartnerName(partner)}`} onClick={() => setDeletingPartner(partner)}>
              <IconControl iconKey="delete" title={`Delete ${getPartnerName(partner)}`} width="1rem" height="1rem" />
            </ButtonControl>
          </span>
        </article>
      ))}
    </div>
  ) : emptyOrLoadingContent("No trading partners have been configured.");

  const tabs = [
    {
      key: "overview",
      title: "Overview",
      content: (
        <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar">
          <section className="resource-workspace-panel resource-workspace-linked-item-section">
            <div className="resource-workspace-section-title">
              <h2>EDI Partner Directory</h2>
              <p>Trading partners are reusable EDI identity records. Inbound and Outbound EDI Resource Configurations assign these partners through partner scopes.</p>
            </div>
            <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
              <div>
                <dt>Partners</dt>
                <dd>{activePartnerCount} active / {partners.length} total</dd>
              </div>
              <div>
                <dt>Editor Source</dt>
                <dd>{partnerMetadata ? "Metadata" : "Loading metadata"}</dd>
              </div>
              <div>
                <dt>Policy Scope</dt>
                <dd>Per Resource Configuration</dd>
              </div>
            </dl>
          </section>

          <section className="resource-workspace-panel resource-workspace-linked-item-section">
            <div className="resource-workspace-section-title">
              <h2>EDI Responsibilities</h2>
              <p>Keep shared partner identity here. Configure document-specific routing, acknowledgement, diagnostics, duplicate handling, and validation on the Resource Configuration partner scope.</p>
            </div>
            <div className="resource-workspace-record-list">
              <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
                <strong>Inbound EDI</strong>
                <small>Partner scopes decide whether documents normalize only or normalize and send to a destination.</small>
              </article>
              <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
                <strong>Outbound EDI</strong>
                <small>Partner scopes decide delivery route and acknowledgement/diagnostic defaults for generated outbound documents.</small>
              </article>
              <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
                <strong>Partner Groups</strong>
                <small>Reusable partner groups are managed separately and assigned from Resource Configuration partner scopes.</small>
              </article>
            </div>
          </section>
        </div>
      )
    },
    {
      key: "partners",
      title: "Partners",
      content: (
        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
            <div>
              <h2>Trading Partners</h2>
              <p>Configured EDI partners that can be assigned to inbound or outbound document flows.</p>
            </div>
            <div className="resource-workspace-section-actions">{searchControl}</div>
          </div>
          {partnerRows}
        </section>
      )
    },
    {
      key: "policies",
      title: "Policies",
      content: (
        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <div className="resource-workspace-section-title">
            <h2>Partner-Scope Policy Model</h2>
            <p>Policies are assigned from each EDI Resource Configuration so the same partner can behave differently by product, document type, and direction.</p>
          </div>
          <div className="resource-workspace-record-list">
            <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
              <strong>Destination Routing</strong>
              <small>Inbound scopes normalize to Entity Schema objects by default. If Send To Destination is selected, the scope requires a destination Resource Configuration.</small>
            </article>
            <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
              <strong>Outbound Delivery</strong>
              <small>Outbound scopes require a delivery route such as AS2, SFTP, OpenText, or a future partner-connectivity resource.</small>
            </article>
            <article className="resource-workspace-record-row resource-workspace-record-row--stacked">
              <strong>Acknowledgements And Diagnostics</strong>
              <small>997/999 behavior, diagnostics, duplicate handling, rejection policy, error queue policy, and test-parse requirements are partner-scope settings.</small>
            </article>
          </div>
        </section>
      )
    }
  ];

  return (
    <main className="resource-workspace resource-workspace-edi-trading-partners" aria-label="EDI trading partner configuration">
      <section className="resource-workspace-screen">
        <div className="resource-workspace-configuration-screen">
          <div className="resource-workspace-configuration-management-header">
            <div className="resource-workspace-configuration-management-title">
              <span className="resource-workspace-header-icon" aria-hidden="true">
                <IconControl iconKey={EDI_TRADING_PARTNER_ICON} title="Trading Partners" width="2.25rem" height="2.25rem" />
              </span>
              <span>
                <span className="resource-workspace-section-eyebrow">EDI</span>
                <h1>Trading Partners</h1>
              </span>
            </div>
          </div>

          <div className="resource-workspace-configuration-command-bar" role="toolbar" aria-label="Trading partner actions">
            <ButtonControl type="standard-small" onClick={loadRows} disabled={loading}>
              <IconControl iconKey="refresh" title="Refresh" width="1rem" height="1rem" />
              Refresh
            </ButtonControl>
            <ButtonControl type="standard-small" onClick={() => setEditingPartner(createPartnerDraft())}>
              <IconControl iconKey="add" title="Add Partner" width="1rem" height="1rem" />
              Add Partner
            </ButtonControl>
            <ButtonControl type="standard-small" onClick={openPartnerGroups}>
              <IconControl iconKey={EDI_TRADING_PARTNER_ICON} title="Partner Groups" width="1rem" height="1rem" />
              Partner Groups
            </ButtonControl>
            {backUrl ? (
              <ButtonControl type="standard-small" className="resource-workspace-command-bar-action resource-workspace-command-bar-action--right" onClick={goBack}>
                <IconControl iconKey="undo" title="Back to Partner Scopes" width="1rem" height="1rem" />
                Back to Partner Scopes
              </ButtonControl>
            ) : null}
          </div>

          <TabPaneControl
            className="resource-workspace-edi-trading-partner-tabs"
            tabs={tabs}
            activeKey={activeTab}
            onChange={(nextIndex, tab) => setActiveTab(tab?.key || "overview")}
            compact={true}
            framed={false}
            align="left"
            unmountInactive={true}
          />
        </div>
      </section>

      {saveError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{saveError}</div> : null}
      <SidePanelFormControl
        open={Boolean(editingPartner)}
        title={getId(editingPartner) ? `Edit Partner: ${getPartnerName(editingPartner)}` : "Add Trading Partner"}
        metadata={partnerMetadata || { Name: "EdiTradingPartner", DisplayName: "EDI Trading Partner", Fields: [] }}
        formFields={getMetadataFields(partnerMetadata)}
        itemData={editingPartner}
        setItemData={setEditingPartner}
        setOpenEditSidePanel={(isOpen) => {
          if (!isOpen) setEditingPartner(null);
        }}
        onUpdateSidePanel={() => {}}
        onCancel={() => setEditingPartner(null)}
        onSave={savePartner}
        saveButtonText="Save"
        viewOnly={!partnerMetadata}
      />
      <DialogControl
        title="Delete Trading Partner"
        openDialog={Boolean(deletingPartner)}
        okText="Delete"
        cancelText="Cancel"
        onOk={deletePartner}
        onCancel={() => setDeletingPartner(null)}
        onX={() => setDeletingPartner(null)}
      >
        <p>Deleting <strong>{getPartnerName(deletingPartner)}</strong> will remove this partner from available EDI partner scope assignment.</p>
        <p>This does not delete Resource Configurations that already exist, but deleted partners will no longer appear in normal partner selection.</p>
      </DialogControl>
    </main>
  );
};

export default EdiTradingPartnerConnectivityManage;
